// ─────────────────────────────────────────────
// LIFESCORE POINT SYSTEM v2
// 2K-style curve: easy early, hard near 100
// Average solid day ~70, perfect day ~95-100
// ─────────────────────────────────────────────

// Base habit weights — tuned so completing all 6 gives ~65-70 raw
const HABIT_WEIGHTS = {
  worked_out:        { health: 18, mind: 5 },
  read_bible:        { faith: 22, mind: 4 },
  reviewed_finances: { career: 14, purpose: 8 },
  meditated:         { mind: 18, faith: 5 },
  reached_out:       { relationships: 22 },
  worked_on_goal:    { career: 16, purpose: 14, mind: 4 },
}

// Curve: solid productive day = 60-75, exceptional = 85+, perfect = 95-100
function applyCurve(raw) {
  if (raw <= 0) return 0
  if (raw >= 100) return 100
  // Gentler curve — rewards real productivity without making 100 impossible
  const x = raw / 100
  const curved = Math.pow(x, 0.75) * 100
  return Math.min(100, Math.round(curved))
}

// Time multiplier — optional, diminishing returns
// minutes: 0=default(1.0), 15=0.85, 30=1.0, 60=1.15, 120=1.25, 180+=1.3
export function getTimeMultiplier(minutes) {
  if (!minutes || minutes <= 0) return 1.0
  if (minutes <= 10)  return 0.7
  if (minutes <= 20)  return 0.85
  if (minutes <= 35)  return 1.0
  if (minutes <= 60)  return 1.12
  if (minutes <= 90)  return 1.20
  if (minutes <= 120) return 1.25
  return 1.30 // cap at 130%
}

// Streak multiplier
export function getStreakMultiplier(streak) {
  if (streak >= 30) return 1.40
  if (streak >= 14) return 1.25
  if (streak >= 7)  return 1.15
  if (streak >= 3)  return 1.05
  return 1.0
}

// Perfect day bonus — all 6 areas covered
function getPerfectDayBonus(areas) {
  const allCovered = Object.values(areas).every(v => v > 0)
  return allCovered ? 1.10 : 1.0
}

export function calculateScores(checkin, habitTimes = {}, manualTaskPoints = {}, streak = 0) {
  const rawAreas = { health: 0, mind: 0, career: 0, relationships: 0, faith: 0, purpose: 0 }

  // Apply habit weights with time multipliers
  Object.entries(HABIT_WEIGHTS).forEach(([habit, impacts]) => {
    if (checkin[habit]) {
      const timeMult = getTimeMultiplier(habitTimes[habit] || 30)
      Object.entries(impacts).forEach(([area, points]) => {
        rawAreas[area] = Math.min(120, rawAreas[area] + (points * timeMult))
      })
    }
  })

  // Add manual task points (already categorized by AI)
  Object.entries(manualTaskPoints).forEach(([area, points]) => {
    if (rawAreas[area] !== undefined) {
      rawAreas[area] = Math.min(120, rawAreas[area] + points)
    }
  })

  // Cap raw at 100 before curve
  Object.keys(rawAreas).forEach(k => { rawAreas[k] = Math.min(100, rawAreas[k]) })

  // Apply 2K curve to each area
  const curvedAreas = {}
  Object.entries(rawAreas).forEach(([area, raw]) => {
    curvedAreas[area] = applyCurve(raw)
  })

  // Calculate raw overall average
  const rawOverall = Object.values(curvedAreas).reduce((a, b) => a + b, 0) / 6

  // Apply multipliers to overall
  const streakMult = getStreakMultiplier(streak)
  const perfectBonus = getPerfectDayBonus(rawAreas)

  const boostedOverall = rawOverall * streakMult * perfectBonus
  const finalOverall = Math.min(100, Math.round(applyCurve(Math.min(100, boostedOverall))))

  return {
    health_score: curvedAreas.health,
    mind_score: curvedAreas.mind,
    career_score: curvedAreas.career,
    relationships_score: curvedAreas.relationships,
    faith_score: curvedAreas.faith,
    purpose_score: curvedAreas.purpose,
    overall_score: finalOverall,
    streak_multiplier: streakMult,
    perfect_day: getPerfectDayBonus(rawAreas) > 1,
  }
}

export function getScoreLabel(score) {
  if (score >= 95) return { label: 'Perfect Day', color: '#f0cc7a' }
  if (score >= 85) return { label: 'Elite', color: '#c9a84c' }
  if (score >= 75) return { label: 'Solid Day', color: '#4caf7d' }
  if (score >= 65) return { label: 'Good Day', color: '#5c8fe0' }
  if (score >= 50) return { label: 'Average', color: '#9b5ce0' }
  if (score >= 30) return { label: 'Below Average', color: '#e07d3c' }
  if (score > 0)   return { label: 'Getting Started', color: '#e05c5c' }
  return { label: 'Start Your Day', color: '#6b6860' }
}

export function getScoreInsight(scores) {
  const areas = [
    { name: 'health',        score: scores.health_score,        label: 'Health',        suggestion: 'Try logging a workout or a healthy meal.' },
    { name: 'mind',          score: scores.mind_score,          label: 'Mind',          suggestion: 'Read, meditate, or learn something new today.' },
    { name: 'career',        score: scores.career_score,        label: 'Career',        suggestion: 'Work on a skill, project, or career task.' },
    { name: 'relationships', score: scores.relationships_score, label: 'Relationships', suggestion: 'Reach out to someone you care about.' },
    { name: 'faith',         score: scores.faith_score,         label: 'Faith',         suggestion: 'Spend a few minutes in prayer or reflection.' },
    { name: 'purpose',       score: scores.purpose_score,       label: 'Goals',         suggestion: 'Make progress on one of your main goals.' },
  ]
  const allZero = areas.every(a => a.score === 0)
  if (allZero) return "You haven't logged anything yet today. Add a task to start earning points."
  const weakest = areas.filter(a => a.score === 0)[0] || [...areas].sort((a, b) => a.score - b.score)[0]
  return `${weakest.label} is your lowest area today. ${weakest.suggestion}`
}

// ── LEVELING SYSTEM ──
export function getLevelFromPoints(totalPoints) {
  let level = 1
  let required = 100
  let accumulated = 0
  while (totalPoints >= accumulated + required) {
    accumulated += required
    level++
    required = Math.floor(required * 1.5)
  }
  const pointsIntoLevel = totalPoints - accumulated
  const progress = Math.min(pointsIntoLevel / required, 1)
  return { level, pointsIntoLevel, pointsRequired: required, progress }
}

export function getLevelTitle(level) {
  if (level >= 50) return 'Legend'
  if (level >= 40) return 'Master'
  if (level >= 30) return 'Elite'
  if (level >= 20) return 'Veteran'
  if (level >= 15) return 'Dedicated'
  if (level >= 10) return 'Committed'
  if (level >= 7)  return 'Consistent'
  if (level >= 5)  return 'Building'
  if (level >= 3)  return 'Rising'
  return 'Beginner'
}
